import { Navigate, createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { PortalLayout } from '@/components/layout/PortalLayout';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { VendorLifecycleListPage } from '@/features/vendors/VendorLifecycleListPage';
import { VendorLifecycleDetailPage } from '@/features/vendors/VendorLifecycleDetailPage';
import { MyApprovalsPage } from '@/features/workflow/MyApprovalsIntegratedPage';
import { WorkflowDefinitionEditorPage, WorkflowDefinitionListPage } from '@/features/workflow/WorkflowIntegratedPages';
import { AuditLogPage } from '@/features/admin/AuditLogPage';
import { LoginPage } from '@/features/portal/PortalPages';
import { PortalOnboardingStatusPage, PortalSubmittedDetailsPage } from '@/features/portal/VendorPortalPages';
import { VendorChangePasswordPage } from '@/features/portal/VendorChangePasswordPage';
import { VendorInvitesPage } from '@/features/vendors/VendorInvitesIntegratedPage';
import { VendorRegistrationPage } from '@/features/portal/VendorRegistrationPage';
import { RequireAnyPermission, RequireAuth, RequirePasswordChanged, RequirePermission } from '@/auth/RouteGuards';
import { ProcessFlowPage } from '@/features/process-flow/ProcessFlowPage';
import { NotificationsPage } from '@/features/admin/NotificationsPage';
import { IamPage } from '@/features/admin/IamPage';
import { MasterDataManagementPage } from '@/features/admin/MasterDataManagementPage';
import { AttachmentsPage } from '@/features/admin/AttachmentsPage';
import { InternalPurchaseOrderDetailPage, InternalPurchaseOrdersPage, VendorPurchaseOrderDetailPage, VendorPurchaseOrdersPage } from '@/features/purchase-orders/PurchaseOrderPages';
import { GoodsReceiptDetailPage, GoodsReceiptsPage } from '@/features/goods-receipts/GoodsReceiptPages';
import { InternalVendorInvoiceDetailPage, PortalVendorInvoiceDetailPage } from '@/features/invoices/VendorInvoicePages';
import { FinanceAdvancesPage, PaymentDetailPage, PaymentsPage } from '@/features/finance/FinancePages';
import { ResetPasswordPage } from '@/features/auth/PasswordRecoveryPages';
import { VendorDashboardPage, VendorGoodsReceiptsPage, VendorHelpPage, VendorInvoicesPage, VendorProfilePage } from '@/features/portal/VendorWorkspacePages';

function NotFound() { return <div className="not-found"><span>404</span><h1>Page not found</h1><p>The page you requested does not exist or you do not have access.</p><a className="button button--primary" href="/app">Return to overview</a></div>; }

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/login" replace /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/portal/login', element: <LoginPage portal /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/register', element: <VendorRegistrationPage /> },
  { path: '/register/:token', element: <VendorRegistrationPage /> },
  { path: '/vendor-registration/:token', element: <VendorRegistrationPage /> },
  { element: <RequireAuth userType="INTERNAL" />, children: [
    { path: '/app', element: <AppLayout />, children: [
      { index: true, element: <DashboardPage /> },
      { path: 'approvals', element: <MyApprovalsPage /> },
      { path: 'process-flow', element: <ProcessFlowPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'attachments', element: <AttachmentsPage /> },
      { path: 'purchase-orders', element: <InternalPurchaseOrdersPage /> },
      { path: 'purchase-orders/:id', element: <InternalPurchaseOrderDetailPage /> },
      { path: 'goods-receipts', element: <GoodsReceiptsPage /> },
      { path: 'goods-receipts/:id', element: <GoodsReceiptDetailPage /> },
      { path: 'invoices/:id', element: <InternalVendorInvoiceDetailPage /> },
      { element: <RequirePermission permission="PAYMENT.VIEW" />, children: [
        { path: 'finance/advances', element: <FinanceAdvancesPage /> },
        { path: 'payments', element: <PaymentsPage /> },
        { path: 'payments/:id', element: <PaymentDetailPage /> },
      ] },
      { element: <RequireAnyPermission permissions={['VENDOR.VIEW', 'VENDOR.CREATE', 'VENDOR.APPROVE']} />, children: [
        { path: 'vendors', element: <VendorLifecycleListPage /> },
        { path: 'vendors/:id', element: <VendorLifecycleDetailPage /> },
      ] },
      { element: <RequirePermission permission="VENDOR.CREATE" />, children: [{ path: 'vendor-invites', element: <VendorInvitesPage /> }] },
      { element: <RequirePermission permission="WORKFLOW.CONFIGURE" />, children: [
        { path: 'workflows', element: <WorkflowDefinitionListPage /> },
        { path: 'workflows/:id', element: <WorkflowDefinitionEditorPage /> },
      ] },
      { element: <RequirePermission permission="USER.MANAGE" />, children: [
        { path: 'users', element: <IamPage /> },
        { path: 'settings/master-data', element: <MasterDataManagementPage /> },
        { path: 'settings/company', element: <MasterDataManagementPage initialTab="companies" /> },
        { path: 'audit', element: <AuditLogPage /> },
      ] },
    ] },
  ] },
  { element: <RequireAuth userType="VENDOR" />, children: [
    { path: '/portal/change-password', element: <VendorChangePasswordPage /> },
    { element: <RequirePasswordChanged />, children: [
      { path: '/portal', element: <PortalLayout />, children: [
        { index: true, element: <VendorDashboardPage /> },
        { path: 'profile', element: <VendorProfilePage /> },
        { path: 'onboarding', element: <PortalOnboardingStatusPage /> },
        { path: 'onboarding/details', element: <PortalSubmittedDetailsPage /> },
        { element: <RequirePermission permission="PORTAL.PO.VIEW" />, children: [
          { path: 'purchase-orders', element: <VendorPurchaseOrdersPage /> },
          { path: 'purchase-orders/:id', element: <VendorPurchaseOrderDetailPage /> },
        ] },
        { path: 'goods-receipts', element: <VendorGoodsReceiptsPage /> },
        { element: <RequirePermission permission="PORTAL.INVOICE.UPLOAD" />, children: [
          { path: 'invoices', element: <VendorInvoicesPage /> },
          { path: 'invoices/:id', element: <PortalVendorInvoiceDetailPage /> },
        ] },
        { element: <RequirePermission permission="PORTAL.PAYMENT.VIEW" />, children: [
          { path: 'payments', element: <PaymentsPage portal /> },
          { path: 'payments/:id', element: <PaymentDetailPage portal /> },
        ] },
        { path: 'notifications', element: <NotificationsPage /> },
        { path: 'help', element: <VendorHelpPage /> },
      ] },
    ] },
  ] },
  { path: '*', element: <NotFound /> },
]);
